// Modale création / édition contact (B-01 + B-04 partiel).
import { useState } from 'react';
import { Building2, User, X } from 'lucide-react';
import { cn } from '../../lib/cn';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import {
  CONTACT_CATEGORY_LABELS,
  type Contact,
  type ContactCategory,
  type ContactKind,
  type CreateContactInput,
} from '../../services/contacts/contactService';
import { useCreateContact, useUpdateContact } from '../../hooks/queries/useContacts';
import { SimilarContactsBanner } from './SimilarContactsBanner';

const CATEGORIES: ContactCategory[] = [
  'client', 'opposing_party', 'expert', 'notary', 'bailiff', 'magistrate', 'other',
];

interface Props {
  contact?: Contact | null;          // si fourni → mode édition
  defaultKind?: ContactKind;
  defaultCategory?: ContactCategory;
  onClose: () => void;
  onSaved?: (contact: Contact) => void;
}

export function ContactEditModal({
  contact, defaultKind = 'individual', defaultCategory = 'client', onClose, onSaved,
}: Props) {
  const isEdit = !!contact;
  const [kind, setKind] = useState<ContactKind>(contact?.kind ?? defaultKind);
  const [firstName, setFirstName] = useState(contact?.first_name ?? '');
  const [lastName, setLastName] = useState(contact?.last_name ?? '');
  const [companyName, setCompanyName] = useState(contact?.company_name ?? '');
  const [siret, setSiret] = useState(contact?.siret ?? '');
  const [email, setEmail] = useState(contact?.email ?? '');
  const [phone, setPhone] = useState(contact?.phone ?? '');
  const [addr1, setAddr1] = useState(contact?.address_line1 ?? '');
  const [addr2, setAddr2] = useState(contact?.address_line2 ?? '');
  const [postalCode, setPostalCode] = useState(contact?.postal_code ?? '');
  const [city, setCity] = useState(contact?.city ?? '');
  const [country, setCountry] = useState(contact?.country ?? 'France');
  const [category, setCategory] = useState<ContactCategory>(contact?.contact_category ?? defaultCategory);
  const [notes, setNotes] = useState(contact?.notes ?? '');

  const create = useCreateContact();
  const update = useUpdateContact();

  function buildPayload(): CreateContactInput {
    return {
      kind,
      first_name:    kind === 'individual' ? (firstName.trim() || null) : null,
      last_name:     kind === 'individual' ? (lastName.trim() || null) : null,
      company_name:  kind === 'corporate' ? (companyName.trim() || null) : null,
      siret:         kind === 'corporate' ? (siret.trim() || null) : null,
      email:         email.trim() || null,
      phone:         phone.trim() || null,
      address_line1: addr1.trim() || null,
      address_line2: addr2.trim() || null,
      postal_code:   postalCode.trim() || null,
      city:          city.trim() || null,
      country:       country.trim() || null,
      contact_category: category,
      notes:         notes.trim() || null,
    };
  }

  const canSave = kind === 'individual'
    ? (firstName.trim() || lastName.trim()).length > 0
    : companyName.trim().length > 0;

  function submit() {
    if (!canSave) return;
    if (isEdit) {
      update.mutate(
        { id: contact.id, patch: buildPayload() },
        { onSuccess: (c) => { onSaved?.(c); onClose(); } },
      );
    } else {
      create.mutate(buildPayload(), {
        onSuccess: (c) => { onSaved?.(c); onClose(); },
      });
    }
  }

  const error = (isEdit ? update.error : create.error) as Error | null;
  const pending = isEdit ? update.isPending : create.isPending;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div
        className="bg-white rounded-cime shadow-xl w-full max-w-xl max-h-[90vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex items-center justify-between px-5 py-4 border-b border-neutral-200">
          <h2 className="font-heading text-lg font-semibold text-cime-text-primary">
            {isEdit ? 'Modifier le contact' : 'Nouveau contact'}
          </h2>
          <button onClick={onClose} className="text-neutral-400 hover:text-neutral-700" aria-label="Fermer">
            <X className="w-4 h-4" />
          </button>
        </header>

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
          {/* Kind toggle */}
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setKind('individual')}
              className={cn(
                'flex-1 inline-flex items-center justify-center gap-2 px-3 py-2 text-sm rounded-md border transition',
                kind === 'individual'
                  ? 'bg-cime-primary text-white border-cime-primary'
                  : 'bg-white border-neutral-200 hover:border-cime-primary/40',
              )}
            >
              <User className="w-4 h-4" /> Personne physique
            </button>
            <button
              type="button"
              onClick={() => setKind('corporate')}
              className={cn(
                'flex-1 inline-flex items-center justify-center gap-2 px-3 py-2 text-sm rounded-md border transition',
                kind === 'corporate'
                  ? 'bg-cime-primary text-white border-cime-primary'
                  : 'bg-white border-neutral-200 hover:border-cime-primary/40',
              )}
            >
              <Building2 className="w-4 h-4" /> Personne morale
            </button>
          </div>

          <SimilarContactsBanner
            input={{
              email,
              first_name: kind === 'individual' ? firstName : null,
              last_name: kind === 'individual' ? lastName : null,
              company_name: kind === 'corporate' ? companyName : null,
              siret: kind === 'corporate' ? siret : null,
              postal_code: postalCode,
              phone,
              exclude_id: contact?.id ?? null,
            }}
          />

          {/* Identity */}
          {kind === 'individual' ? (
            <div className="grid grid-cols-2 gap-3">
              <Field label="Prénom">
                <Input value={firstName} onChange={(e) => setFirstName(e.target.value)} autoFocus />
              </Field>
              <Field label="Nom">
                <Input value={lastName} onChange={(e) => setLastName(e.target.value)} />
              </Field>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-3">
              <Field label="Raison sociale">
                <Input value={companyName} onChange={(e) => setCompanyName(e.target.value)} autoFocus />
              </Field>
              <Field label="SIRET">
                <Input value={siret} onChange={(e) => setSiret(e.target.value)} placeholder="123 456 789 00012" />
              </Field>
            </div>
          )}

          {/* Catégorie */}
          <Field label="Catégorie">
            <div className="grid grid-cols-4 gap-1.5">
              {CATEGORIES.map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => setCategory(c)}
                  className={cn(
                    'px-2 py-1.5 text-xs rounded-md border transition',
                    category === c
                      ? 'bg-cime-primary text-white border-cime-primary'
                      : 'bg-white border-neutral-200 hover:border-cime-primary/40',
                  )}
                >
                  {CONTACT_CATEGORY_LABELS[c]}
                </button>
              ))}
            </div>
          </Field>

          {/* Contact */}
          <div className="grid grid-cols-2 gap-3">
            <Field label="Email">
              <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
            </Field>
            <Field label="Téléphone">
              <Input value={phone} onChange={(e) => setPhone(e.target.value)} />
            </Field>
          </div>

          {/* Adresse */}
          <Field label="Adresse">
            <Input value={addr1} onChange={(e) => setAddr1(e.target.value)} placeholder="N° et rue" className="mb-1.5" />
            <Input value={addr2} onChange={(e) => setAddr2(e.target.value)} placeholder="Complément (bâtiment, étage…)" />
          </Field>
          <div className="grid grid-cols-3 gap-3">
            <Field label="Code postal">
              <Input value={postalCode} onChange={(e) => setPostalCode(e.target.value)} />
            </Field>
            <Field label="Ville">
              <Input value={city} onChange={(e) => setCity(e.target.value)} />
            </Field>
            <Field label="Pays">
              <Input value={country} onChange={(e) => setCountry(e.target.value)} />
            </Field>
          </div>

          {/* Notes */}
          <Field label="Notes internes">
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              className="w-full px-3 py-2 text-sm border border-neutral-300 rounded-cime focus:outline-none focus:ring-2 focus:ring-cime-primary/20"
            />
          </Field>

          {error && (
            <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md p-2.5">
              {error.message}
            </div>
          )}
        </div>

        <footer className="flex items-center justify-end gap-2 px-5 py-3 border-t border-neutral-200 bg-neutral-50">
          <Button variant="ghost" onClick={onClose} disabled={pending}>Annuler</Button>
          <Button onClick={submit} disabled={!canSave || pending}>
            {pending ? 'Enregistrement…' : isEdit ? 'Enregistrer' : 'Créer le contact'}
          </Button>
        </footer>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="block text-[11px] uppercase tracking-wider text-neutral-500 mb-1">
        {label}
      </label>
      {children}
    </div>
  );
}
